import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import { auth } from '../firebase';
import { signOut } from 'firebase/auth';
import SEO from '../components/SEO';

const companyName = process.env.REACT_APP_COMPANY_NAME || 'Autopilot';
const companyEmail = process.env.REACT_APP_COMPANY_EMAIL || 'support@autopilot.co.zw';

// External reference link (Meta, Groq, etc.) — opens in a new tab
function ExtLink({ href, children }: { href: string; children: React.ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#818cf8', textDecoration: 'underline', textUnderlineOffset: '2px' }}>
      {children} ↗
    </a>
  );
}

/* ─────────────────────────────────────────────
   STYLES
───────────────────────────────────────────── */
const S = {
  page: { display: 'flex', minHeight: 'calc(100vh - 64px)', background: 'var(--bg-page)', fontFamily: "'DM Sans', system-ui, sans-serif" } as React.CSSProperties,
  sidebar: (collapsed: boolean): React.CSSProperties => ({
    width: collapsed ? '72px' : '280px',
    background: 'var(--bg-sidebar)',
    borderRight: '1px solid var(--border-subtle)',
    display: 'flex', flexDirection: 'column', flexShrink: 0,
    position: 'relative', transition: 'width 0.3s cubic-bezier(.4,0,.2,1)',
  }),
  main: { flex: 1, overflow: 'auto', padding: '36px 32px', background: 'var(--bg-page)' } as React.CSSProperties,
  card: { background: 'var(--bg-card)', borderRadius: '16px', border: '1px solid var(--bg-element)', padding: '28px', marginBottom: '20px' } as React.CSSProperties,
  h1: { color: 'var(--text-primary)', fontSize: '26px', fontWeight: '700', letterSpacing: '-0.02em', margin: '0 0 8px' } as React.CSSProperties,
  h2: { color: 'var(--text-primary)', fontSize: '18px', fontWeight: '700', margin: '0 0 16px', display: 'flex', alignItems: 'center', gap: '8px' } as React.CSSProperties,
  h3: { color: '#cbd5e1', fontSize: '14px', fontWeight: '600', margin: '0 0 6px' } as React.CSSProperties,
  p: { color: 'var(--text-muted)', fontSize: '14px', lineHeight: '1.7', margin: '0 0 12px' } as React.CSSProperties,
  code: { background: 'var(--bg-page)', border: '1px solid var(--bg-element)', borderRadius: '8px', padding: '14px 16px', fontFamily: 'monospace', fontSize: '12px', color: '#818cf8', display: 'block', lineHeight: '1.7', whiteSpace: 'pre-wrap', overflowX: 'auto' } as React.CSSProperties,
  tip: { background: '#f59e0b0d', border: '1px solid #f59e0b30', borderLeft: '3px solid #f59e0b', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' } as React.CSSProperties,
  warning: { background: '#ef44440d', border: '1px solid #ef444430', borderLeft: '3px solid #ef4444', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' } as React.CSSProperties,
  success: { background: '#10b9810d', border: '1px solid #10b98130', borderLeft: '3px solid #10b981', borderRadius: '8px', padding: '12px 16px', marginBottom: '16px', fontSize: '13px', color: 'var(--text-secondary)', lineHeight: '1.6' } as React.CSSProperties,
  step: { display: 'flex', gap: '14px', alignItems: 'flex-start', padding: '16px', background: 'var(--bg-sidebar)', borderRadius: '10px', marginBottom: '10px', border: '1px solid var(--bg-element)' } as React.CSSProperties,
  stepNum: { width: '28px', height: '28px', borderRadius: '50%', background: '#5B4FE8', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '700', flexShrink: 0, marginTop: '1px' } as React.CSSProperties,
  badge: (color: string) => ({ display: 'inline-block', background: color + '20', color, fontSize: '10px', fontWeight: '700', padding: '3px 8px', borderRadius: '10px', letterSpacing: '0.04em' }),
  table: { width: '100%', borderCollapse: 'collapse' as const, fontSize: '13px' },
  th: { textAlign: 'left' as const, padding: '10px 12px', color: 'var(--text-faint)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '0.06em', borderBottom: '1px solid var(--bg-element)' },
  td: { padding: '10px 12px', color: 'var(--text-secondary)', borderBottom: '1px solid var(--bg-element)', verticalAlign: 'top' as const },
  tdCode: { padding: '10px 12px', fontFamily: 'monospace', fontSize: '12px', color: '#818cf8', borderBottom: '1px solid var(--bg-element)' },
};

/* ─────────────────────────────────────────────
   SECTION DATA
───────────────────────────────────────────── */
const sections = [
  { id: 'overview', icon: '🚀', title: 'Overview', group: 'Getting Started' },
  { id: 'creating-bot', icon: '🤖', title: 'Creating a Bot', group: 'Getting Started' },
  { id: 'bot-types', icon: '📱', title: 'Bot Types', group: 'Getting Started' },
  { id: 'dashboard', icon: '📊', title: 'Dashboard', group: 'Using ZimChat' },
  { id: 'flow-builder', icon: '🔀', title: 'Flow Builder', group: 'Using ZimChat' },
  { id: 'response-types', icon: '💬', title: 'Response Types', group: 'Using ZimChat' },
  { id: 'templates', icon: '📋', title: 'Templates', group: 'Using ZimChat' },
  { id: 'website-bot', icon: '🌐', title: 'Website Bot', group: 'Bot Channels' },
  { id: 'whatsapp-bot', icon: '💬', title: 'WhatsApp Bot', group: 'Bot Channels' },
  { id: 'facebook-bot', icon: '📘', title: 'Facebook Bot', group: 'Bot Channels' },
  { id: 'voice-bot', icon: '🎙️', title: 'Voice Bot', group: 'Bot Channels' },
  { id: 'ai-bot', icon: '🤖', title: 'AI Bot', group: 'Bot Channels' },
  { id: 'advanced-wa', icon: '📡', title: 'Dynamic Lists & Responses', group: 'Advanced' },
  { id: 'order-handler', icon: '🛒', title: 'Order Handler', group: 'Advanced' },
  { id: 'bookings-system', icon: '📅', title: 'Inventory & Bookings', group: 'Advanced' },
  { id: 'webhooks', icon: '🌐', title: 'Webhooks & API', group: 'Advanced' },
  { id: 'analytics', icon: '📈', title: 'Analytics', group: 'Advanced' },
  { id: 'troubleshooting', icon: '🔧', title: 'Troubleshooting', group: 'Help' },
  { id: 'faq', icon: '❓', title: 'FAQ', group: 'Help' },
];

/* ─────────────────────────────────────────────
   SECTION CONTENT COMPONENTS
───────────────────────────────────────────── */
function Overview() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🚀 What is {companyName}?</h2>
        <p style={S.p}>{companyName} lets any business set up an automated bot that chats with customers — on WhatsApp, their website, Facebook, or as a voice assistant. No coding required. You build the conversation, we handle the rest.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
          {[
            { icon: '💬', label: 'WhatsApp Bot', desc: 'Chat with customers on WhatsApp 24/7' },
            { icon: '🌐', label: 'Website Bot', desc: 'Add a chat widget to your website' },
            { icon: '📘', label: 'Facebook Bot', desc: 'Auto-reply on your Facebook page' },
            { icon: '🎙️', label: 'Voice Bot', desc: 'AI voice assistant for calls' },
            { icon: '🤖', label: 'AI Bot', desc: 'Knowledge-base powered AI that answers in your own words (Business plan+)' },
          ].map(c => (
            <div key={c.label} style={{ background: 'var(--bg-sidebar)', borderRadius: '10px', border: '1px solid var(--bg-element)', padding: '16px' }}>
              <div style={{ fontSize: '24px', marginBottom: '8px' }}>{c.icon}</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '4px' }}>{c.label}</div>
              <div style={{ color: 'var(--text-faint)', fontSize: '12px' }}>{c.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>⚡ How it works in 3 steps</h2>
        {[
          { n: '1', t: 'Create a bot', d: 'Pick the channel (WhatsApp, website, etc.), fill in your business details, and choose an industry template to start from.' },
          { n: '2', t: 'Build your flow', d: 'Use the Flow Builder to set up triggers and responses. A trigger is a word customers type; a response is what your bot says back.' },
          { n: '3', t: 'Go live', d: 'For website bots, paste the embed code on your site. For WhatsApp, we register your number with Meta. That\'s it.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div>
              <div style={S.h3}>{s.t}</div>
              <p style={{ ...S.p, margin: 0 }}>{s.d}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>💳 Plans</h2>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {['Plan', 'Monthly', 'Yearly', 'Setup fee', 'Bots', 'Conversations', 'Support'].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {[
                ['Free', '$0', '$0', '$0', '1 bot (website only)', '100/month', 'Community'],
                ['Starter', '$45/mo', '$432/yr', '$25', '1 bot (any channel)', '500/month', 'Email'],
                ['Business', '$75/mo', '$720/yr', '$50', '3 bots (any channel)', '2,000/month', 'Priority'],
                ['Agency', '$200/mo', '$1,920/yr', '$100', '10 bots (any channel)', '10,000/month', 'Dedicated'],
                ['Enterprise', 'Contact us', 'Contact us', '—', 'Unlimited', 'Unlimited', 'Dedicated account manager'],
              ].map(row => (
                <tr key={row[0]}>
                  {row.map((cell, i) => <td key={i} style={S.td}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div style={{ ...S.tip, marginTop: '16px', marginBottom: 0 }}>
          💡 All paid plans include a one-time setup fee. No long-term contracts — cancel any time. AI Bots (knowledge-base powered, see "AI Bot" in Bot Channels) require Business plan or above.
        </div>
      </div>
    </>
  );
}

function CreatingBot() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🤖 Creating your first bot</h2>
        <p style={S.p}>From the dashboard, click <strong style={{ color: 'var(--text-primary)' }}>+ New Bot</strong> in the sidebar or the create button. You'll go through a 3-step wizard.</p>

        {[
          { n: '1', t: 'Choose your bot type', d: 'Pick where your customers talk to you — WhatsApp, Website, Facebook, or Voice. You can create different bots for different channels.' },
          { n: '2', t: 'Fill in your business details', d: 'Business name, industry, welcome message, and channel-specific info (phone number for WhatsApp, page credentials for Facebook, website URL for website/voice).' },
          { n: '3', t: 'Pick a template', d: 'Choose from industry templates (Barber Shop, Restaurant, Real Estate, etc.) to start with pre-built conversation flows. You can edit everything after.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><p style={{ ...S.p, margin: 0 }}>{s.d}</p></div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📋 Industry templates</h2>
        <p style={S.p}>When you pick a template, your bot is pre-filled with relevant triggers and responses for your industry. Available templates:</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '10px' }}>
          {['💈 Barber Shop', '💅 Beauty Salon', '🍕 Restaurant', '👕 Clothing Store', '🏠 Real Estate', '🎨 Custom (blank)'].map(t => (
            <div key={t} style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--bg-element)', borderRadius: '8px', padding: '12px', fontSize: '13px', color: 'var(--text-secondary)' }}>{t}</div>
          ))}
        </div>
        <div style={{ ...S.tip, marginTop: '16px', marginBottom: 0 }}>
          💡 Templates are a starting point. You can edit, add, and delete any trigger or response after your bot is created.
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>⚙️ Bot settings</h2>
        <p style={S.p}>After creating a bot, access its settings from the Dashboard by clicking the <strong style={{ color: 'var(--text-primary)' }}>Settings</strong> button in the top bar. From there you can:</p>
        <ul style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: '2', paddingLeft: '20px' }}>
          <li>Change the business name and welcome message</li>
          <li>Update Phone Number ID and Access Token (WhatsApp)</li>
          <li>Copy your webhook URL for Meta setup</li>
          <li>Manage allowed websites (website bot)</li>
          <li>Copy your embed code (website bot)</li>
          <li>Delete the bot</li>
        </ul>
      </div>
    </>
  );
}

function BotTypes() {
  return (
    <>
      {[
        {
          icon: '💬', title: 'WhatsApp Bot', color: '#10b981',
          desc: 'Your bot gets a WhatsApp Business API number. Customers message it on WhatsApp and get instant automated replies.',
          setup: 'You provide your business name, address, email, and the phone number you want to use. We submit it to Meta for WhatsApp Business API access. Approval takes 1–3 business days.',
          features: ['Text messages with WhatsApp markdown', 'Interactive buttons (max 3)', 'Scrollable lists', 'Images, documents', 'Ask questions and collect answers', 'Dynamic product lists from your API', 'Order processing'],
          note: 'The phone number must NOT already be on personal WhatsApp. Once registered, it moves to Business API.',
        },
        {
          icon: '🌐', title: 'Website Bot', color: '#3b82f6',
          desc: 'A chat bubble appears in the corner of your website. Visitors click it to chat with your bot.',
          setup: 'Provide your website URL and email. We give you a one-line script tag to paste into your site. No coding beyond that.',
          features: ['Text, buttons, images, video', 'Link / CTA buttons', 'Ask visitor questions', 'Hand off to human agent', 'Keyword aliases (multiple words per trigger)', 'Widget settings (greeting, offline message)', 'Embed code with optional colour/position customisation'],
          note: 'The widget loads on any website. You can restrict it to specific domains in settings.',
        },
        {
          icon: '📘', title: 'Facebook Bot', color: '#1877f2',
          desc: 'Automatically replies to messages sent to your Facebook business page.',
          setup: 'Provide your Facebook Page ID and Page Access Token from Meta Developer Console.',
          features: ['Auto-replies with keyword triggers', 'Quick reply buttons (max 3)', 'Persistent menu (always-visible chat menu)', 'Get-started message for new users'],
          note: 'You need admin access to the Facebook Page and must generate a Page Access Token with pages_messaging permission.',
        },
        {
          icon: '🎙️', title: 'Voice Bot', color: '#8b5cf6',
          desc: 'An AI voice assistant on your website. Visitors can speak to it and get spoken responses.',
          setup: 'Our team configures the voice bot after you provide your website URL. You get an embed code by email. Setup takes 2–5 business days.',
          features: ['Say (bot speaks text)', 'Ask (collects spoken answer)', 'Branch (IVR menu — route by what caller says)', 'Transfer to live agent', 'End call', 'Language selection (English, Shona, Ndebele)', 'Man/woman voice selection'],
          note: 'Voice bots use text-to-speech to read your scripts aloud. Write scripts as you\'d naturally say them.',
        },
        {
          icon: '🤖', title: 'AI Bot', color: '#f59e0b',
          desc: 'Instead of fixed triggers and responses, an AI Bot answers customer questions in natural language using a knowledge base you write. Available on WhatsApp, Website, or Facebook.',
          setup: 'Available on Business plan and above. Pick "AI Bot" + a channel in the bot creation wizard, paste in a free Groq API key, then fill in your Knowledge Base, Tone, and any channel credentials from Bot Settings.',
          features: ['Free Groq API key (no credit card)', '4 tone presets: Friendly, Marketer, Sales-driven, Straight to the point', 'Custom system instructions', 'Knowledge base (FAQs, pricing, policies)', 'Never answers outside the knowledge base — no hallucinated prices or facts'],
          note: 'Only answers from what you put in the Knowledge Base. If it doesn\'t know, it tells the customer to contact you directly rather than guessing.',
        },
      ].map(bt => (
        <div key={bt.title} style={{ ...S.card, borderLeft: `3px solid ${bt.color}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '12px' }}>
            <span style={{ fontSize: '24px' }}>{bt.icon}</span>
            <h2 style={{ ...S.h2, margin: 0 }}>{bt.title}</h2>
            <span style={S.badge(bt.color)}>{bt.title.split(' ')[0].toLowerCase()}</span>
          </div>
          <p style={S.p}>{bt.desc}</p>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ color: 'var(--text-faint)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '6px' }}>Setup</div>
            <p style={{ ...S.p, margin: 0 }}>{bt.setup}</p>
          </div>
          <div style={{ marginBottom: '14px' }}>
            <div style={{ color: 'var(--text-faint)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '8px' }}>Features</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {bt.features.map(f => (
                <span key={f} style={{ background: 'var(--bg-element)', color: 'var(--text-secondary)', fontSize: '11px', padding: '3px 10px', borderRadius: '10px' }}>{f}</span>
              ))}
            </div>
          </div>
          <div style={S.warning}>⚠️ {bt.note}</div>
        </div>
      ))}
    </>
  );
}

function DashboardDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>📊 Dashboard overview</h2>
        <p style={S.p}>The dashboard is your command centre. The sidebar on the left lets you navigate between views and switch between bots.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {[
            { icon: '📊', label: 'Overview tab', desc: 'Summary stats, message chart, and recent conversations for the selected bot.' },
            { icon: '💬', label: 'Conversations tab', desc: 'Full conversation history. Filter by phone number or date. Export as CSV.' },
            { icon: '📈', label: 'Reports tab', desc: 'Performance stats: avg messages/day, best day, messages per user. Download CSV.' },
            { icon: '🔌', label: 'Embed Code tab', desc: 'Website bots only. Copy your embed snippet and view install instructions.' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', gap: '12px', padding: '12px', background: 'var(--bg-sidebar)', borderRadius: '9px', border: '1px solid var(--bg-element)' }}>
              <span style={{ fontSize: '18px' }}>{item.icon}</span>
              <div>
                <div style={S.h3}>{item.label}</div>
                <p style={{ ...S.p, margin: 0 }}>{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🤖 Switching between bots</h2>
        <p style={S.p}>All your bots appear in the sidebar under "My Bots". Each bot shows an icon for its type (💬 WhatsApp, 🌐 Website, 📘 Facebook, 🎙️ Voice) and a coloured dot indicating status:</p>
        <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
          {[['#10b981', 'Live — bot is active'], ['#f59e0b', 'Pending — awaiting setup/verification'], ['#64748b', 'Inactive']].map(([c, l]) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: 'var(--text-secondary)' }}>
              <div style={{ width: '8px', height: '8px', borderRadius: '3px', background: c as string }} />
              {l}
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📊 Understanding your stats</h2>
        <table style={S.table}>
          <thead><tr>{['Metric', 'What it means'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['Total Messages', 'Every message sent or received by your bot'],
              ['Customers / Visitors', 'Number of unique people who have chatted with your bot'],
              ['Auto-handled %', 'Percentage of messages handled without needing a human'],
              ['Est. Savings', 'Estimated cost saved vs. manual customer service (at $0.50/message)'],
            ].map(([m, d]) => <tr key={m}><td style={S.td}><strong style={{ color: 'var(--text-primary)' }}>{m}</strong></td><td style={S.td}>{d}</td></tr>)}
          </tbody>
        </table>
      </div>
    </>
  );
}

function FlowBuilderDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🔀 What is the Flow Builder?</h2>
        <p style={S.p}>The Flow Builder is where you build conversations. It works like this: when a customer types a word (<strong style={{ color: '#818cf8' }}>trigger</strong>), your bot sends a sequence of replies (<strong style={{ color: '#818cf8' }}>responses</strong>).</p>
        <div style={S.success}>✅ Every bot type has its own Flow Builder tailored to that channel — WhatsApp has lists and buttons, voice has speak/ask/branch, website has link buttons and hand-off.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🎯 Triggers</h2>
        <p style={S.p}>A trigger is a keyword that starts a conversation flow. When a customer's message contains that word, the bot runs that flow.</p>
        <pre style={S.code}>{`Trigger word: "pricing"
→ Matched when customer types: "what's the pricing?", "pricing please", "I need pricing info"

Trigger word: "hi"  
→ Matched when customer types: "hi", "hello", "hey" (if you add keyword aliases)`}</pre>
        <div style={S.tip}>💡 Website and Voice bots also support <strong>keyword aliases</strong> — comma-separated extra words that also match the trigger. E.g. trigger "pricing" with aliases "cost, price, how much".</div>
        <div style={S.warning}>⚠️ Keep triggers specific. "our" appears in many sentences and would match unintentionally. Use "hours" or "opening_hours" instead.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📨 Responses</h2>
        <p style={S.p}>Each trigger can have multiple responses which are sent in order, one after another. You can add, reorder (↑), and delete responses.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          {[
            { label: '+ Add trigger', desc: 'Type a trigger word and optional first response, press Enter or click Add.' },
            { label: '▸ / ▾', desc: 'Expand or collapse a trigger card to show/hide its responses.' },
            { label: '📋 Duplicate', desc: 'Copy a trigger and all its responses (useful for similar flows).' },
            { label: '+ Add response', desc: 'Add another message to the sequence for that trigger.' },
            { label: '↑ Up', desc: 'Move a response earlier in the sequence.' },
            { label: '💾 Save flow', desc: 'Always save before leaving the builder. Changes are not auto-saved.' },
          ].map(item => (
            <div key={item.label} style={{ display: 'flex', gap: '12px', alignItems: 'flex-start', padding: '10px', background: 'var(--bg-sidebar)', borderRadius: '8px', border: '1px solid var(--bg-element)' }}>
              <code style={{ background: 'var(--bg-element)', color: '#818cf8', padding: '2px 8px', borderRadius: '6px', fontSize: '12px', flexShrink: 0, fontFamily: 'monospace' }}>{item.label}</code>
              <p style={{ ...S.p, margin: 0 }}>{item.desc}</p>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>💡 Example flow</h2>
        <pre style={S.code}>{`Trigger: "booking"

Response 1 — Ask:
  "What date would you like to book?"
  Save answer as: booking_date

Response 2 — Ask:
  "What time works best?"
  Save answer as: booking_time

Response 3 — Text:
  "✅ Booking request for {{booking_date}} at {{booking_time}} received!
   We'll confirm shortly."

→ When customer types "booking", they get asked for date, then time,
  then a personalised confirmation with their answers inserted.`}</pre>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔗 Loading a template into an existing bot</h2>
        <p style={S.p}>Inside any Flow Builder, click <strong style={{ color: 'var(--text-primary)' }}>📋 Load template</strong> in the top bar. A panel opens showing all templates for that bot type. Click a template and confirm — it replaces your current flow.</p>
        <div style={S.warning}>⚠️ Loading a template replaces your current flow. Save first if you want to keep it.</div>
      </div>
    </>
  );
}

function ResponseTypesDoc() {
  const wa = [
    { icon: '💬', name: 'Text', desc: 'Plain message. Supports WhatsApp markdown: *bold*, _italic_, ~strikethrough~. Use {{variableName}} to insert collected answers.', example: 'Thanks {{name}}! Your order is confirmed.' },
    { icon: '🖼️', name: 'Image', desc: 'Sends an image. Provide a public HTTPS URL. Add an optional caption.', example: 'https://yoursite.com/product.jpg' },
    { icon: '📄', name: 'Document', desc: 'Sends a PDF, Word, or other file. Provide a public HTTPS URL and filename.', example: 'https://yoursite.com/catalog.pdf' },
    { icon: '🔘', name: 'Buttons', desc: 'Up to 3 clickable buttons. When clicked, the button\'s payload is sent as a message (matching another trigger).', example: 'Buttons: "Book now" → payload "booking"' },
    { icon: '📋', name: 'List', desc: 'A scrollable list with sections and rows. Each row has a title (max 24 chars) and payload. Best for menus with 4+ options.', example: 'Services: Haircut, Shave, Beard Trim' },
    { icon: '❓', name: 'Ask', desc: 'Bot asks a question, waits for reply, saves the answer as a variable. Use {{variableName}} in later Text responses.', example: 'What is your name? → saves as {{customer_name}}' },
    { icon: '🔗', name: 'Chain', desc: 'After sending this, the bot auto-sends a trigger word after a delay (in seconds). Useful for guided flows.', example: 'After welcome, auto-send "help" after 2 seconds' },
    { icon: '🌐', name: 'Webhook', desc: 'Calls an external API URL. Returns a value you can show in the response.', example: 'GET https://api.yoursite.com/status' },
    { icon: '📡', name: 'Dynamic List', desc: 'Fetches items from your API and shows them as a WhatsApp list. Items update dynamically.', example: 'GET /api/products → list of products' },
    { icon: '🔗', name: 'Dynamic Response', desc: 'Shows full details of an item selected from a Dynamic List (image, price, description, action buttons).', example: 'GET /api/product/{{id}} → product detail' },
    { icon: '🛒', name: 'Order Handler', desc: 'Complete order flow: collects customer info, quantity, then generates payment link / notifies admin / calls API.', example: 'Name → Phone → Quantity → Payment link' },
  ];
  const web = [
    { icon: '💬', name: 'Text', desc: 'Plain message with optional {{variable}} substitution.' },
    { icon: '🔘', name: 'Buttons', desc: 'Up to 3 buttons. Payload matches a trigger word to continue the flow.' },
    { icon: '🖼️', name: 'Image', desc: 'Displays an image with optional caption.' },
    { icon: '🎬', name: 'Video', desc: 'Embeds or links to a video.' },
    { icon: '🔗', name: 'Link / CTA', desc: 'Shows a message with a clickable link button (e.g. "Book now", "View menu").' },
    { icon: '❓', name: 'Ask', desc: 'Asks a question inline in the chat. Stores the typed answer as a variable.' },
    { icon: '🙋', name: 'Hand off to human', desc: 'Ends the bot conversation and emails your support address.' },
  ];
  const voice = [
    { icon: '🔊', name: 'Say', desc: 'Bot speaks this text aloud (text-to-speech). Supports {{variable}} substitution.' },
    { icon: '❓', name: 'Ask', desc: 'Bot asks a question and saves the spoken answer as a variable.' },
    { icon: '🔀', name: 'Branch', desc: 'IVR menu — bot presents options and routes to different triggers based on what caller says.' },
    { icon: '📞', name: 'Transfer', desc: 'Bot says a message then transfers the call to a phone number.' },
    { icon: '📴', name: 'End call', desc: 'Bot says goodbye and hangs up.' },
  ];
  const fb = [
    { icon: '💬', name: 'Auto-reply', desc: 'Text reply with optional quick reply buttons (max 3, 20 char limit).' },
    { icon: '☰', name: 'Persistent Menu', desc: 'Always-visible menu in Messenger (max 3 items). Can trigger a reply or open a URL.' },
    { icon: '👋', name: 'Get-started message', desc: 'Sent when a new user clicks "Get Started" for the first time.' },
  ];

  return (
    <>
      {[
        { title: '💬 WhatsApp response types', items: wa },
        { title: '🌐 Website response types', items: web },
        { title: '🎙️ Voice response types', items: voice },
        { title: '📘 Facebook response types', items: fb },
      ].map(section => (
        <div key={section.title} style={S.card}>
          <h2 style={S.h2}>{section.title}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {section.items.map(item => (
              <div key={item.name} style={{ display: 'flex', gap: '12px', padding: '12px', background: 'var(--bg-sidebar)', borderRadius: '9px', border: '1px solid var(--bg-element)' }}>
                <span style={{ fontSize: '18px', flexShrink: 0 }}>{item.icon}</span>
                <div>
                  <>
                    <div style={{ ...S.h3, marginBottom: '3px' }}>{item.name}</div>
                    <p style={{ ...S.p, margin: 0 }}>{item.desc}</p>
                    {'example' in item && item.example && (
                      <div style={{ marginTop: '6px', background: 'var(--bg-element)', borderRadius: '6px', padding: '6px 10px', fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'monospace' }}>
                        {`Example: ${item.example}`}
                      </div>
                    )}
                  </>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function TemplatesDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>📋 What are templates?</h2>
        <p style={S.p}>Templates are pre-built conversation flows saved in the database. They're tagged by bot type (WhatsApp, Website, Voice, Facebook) so the right templates appear in the right builder.</p>
        <p style={S.p}>Templates let you:</p>
        <ul style={{ color: 'var(--text-muted)', fontSize: '14px', lineHeight: '2.2', paddingLeft: '20px' }}>
          <li>Start a new bot with a proven conversation flow instead of blank</li>
          <li>Save your own flows as reusable templates for future bots</li>
          <li>Share flows across your team</li>
        </ul>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🗂️ Template Manager</h2>
        <p style={S.p}>Go to <strong style={{ color: 'var(--text-primary)' }}>/templates</strong> (link in the sidebar) to manage all templates.</p>
        {[
          { n: '1', t: 'Add starter templates', d: 'First time? Click "✨ Add starter templates" to seed the database with 10 pre-built templates (Barber Shop, Restaurant, Business IVR, etc.).' },
          { n: '2', t: 'Create a new template', d: 'Click "+ New template", fill in the name, description, icon, and bot type. Then click "Edit flow" to build the actual conversation.' },
          { n: '3', t: 'Edit a template flow', d: 'Click "Edit flow" on any template card. It opens the correct builder for that template\'s bot type, reading from the templates database node.' },
          { n: '4', t: 'Duplicate a template', d: 'Click the 📋 icon to copy a template with "(copy)" appended to the name. Useful for creating variations.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><p style={{ ...S.p, margin: 0 }}>{s.d}</p></div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📥 Loading a template into a bot</h2>
        <p style={S.p}>In any Flow Builder (WhatsApp, Website, Voice, Facebook), click <strong style={{ color: 'var(--text-primary)' }}>📋 Load template</strong> in the top bar. A modal appears showing templates for that bot type. Click one, confirm, and the flow is loaded.</p>
        <div style={S.tip}>💡 The "Load template" button only shows templates tagged for that bot type. WhatsApp templates appear in the WhatsApp builder, Website templates in the Website builder, etc.</div>
        <div style={S.warning}>⚠️ Loading a template overwrites your current flow. Make sure you've saved anything you want to keep first.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔥 Starter templates included</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '10px' }}>
          {[
            { icon: '💈', name: 'Barber Shop', type: 'whatsapp', color: '#10b981' },
            { icon: '💅', name: 'Beauty Salon', type: 'whatsapp', color: '#10b981' },
            { icon: '🍕', name: 'Restaurant', type: 'whatsapp', color: '#10b981' },
            { icon: '👕', name: 'Clothing Store', type: 'whatsapp', color: '#10b981' },
            { icon: '🏠', name: 'Real Estate', type: 'whatsapp', color: '#10b981' },
            { icon: '🏢', name: 'Business Website', type: 'website', color: '#3b82f6' },
            { icon: '🛒', name: 'Online Retail', type: 'website', color: '#3b82f6' },
            { icon: '🍽️', name: 'Restaurant Website', type: 'website', color: '#3b82f6' },
            { icon: '📞', name: 'Business IVR', type: 'voice', color: '#8b5cf6' },
            { icon: '🏥', name: 'Medical Practice', type: 'voice', color: '#8b5cf6' },
          ].map(t => (
            <div key={t.name} style={{ background: 'var(--bg-sidebar)', border: `1px solid ${t.color}30`, borderRadius: '9px', padding: '12px', display: 'flex', gap: '10px', alignItems: 'center' }}>
              <span style={{ fontSize: '20px' }}>{t.icon}</span>
              <div>
                <div style={{ color: 'var(--text-primary)', fontSize: '13px', fontWeight: '600' }}>{t.name}</div>
                <div style={{ ...S.badge(t.color), marginTop: '4px' }}>{t.type}</div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

function WebsiteBotDoc() {
  const API_BASE = process.env.NODE_ENV === 'development' ? '' : (process.env.REACT_APP_API_URL || '');
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🌐 Website bot overview</h2>
        <p style={S.p}>The website bot adds a chat bubble to your site. Visitors click it to chat. The bot responds based on the triggers and responses you've set up in the Website Flow Builder.</p>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔌 Installing the widget</h2>
        {[
          { n: '1', t: 'Get your embed code', d: 'In the dashboard, select your website bot, go to the Embed Code tab, and copy the script tag.' },
          { n: '2', t: 'Paste it on your site', d: 'Open your website\'s HTML editor (or theme footer in WordPress/Wix/Squarespace). Paste the script just before the closing </body> tag.' },
          { n: '3', t: 'Publish and test', d: 'Save and publish your site. The chat bubble should appear in the bottom-right corner.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><p style={{ ...S.p, margin: 0 }}>{s.d}</p></div>
          </div>
        ))}
        <pre style={{ ...S.code, marginTop: '16px' }}>{`<!-- Basic -->
<script src="${API_BASE}/widget.js" data-bot-id="YOUR_BOT_ID"></script>

<!-- With options -->
<script
  src="${API_BASE}/widget.js"
  data-bot-id="YOUR_BOT_ID"
  data-primary-color="#5B4FE8"
  data-position="bottom-right"
  data-business-name="My Business"
></script>`}</pre>
        <table style={{ ...S.table, marginTop: '12px' }}>
          <thead><tr>{['Attribute', 'Default', 'Description'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['data-bot-id', '(required)', 'Your bot\'s unique ID from the dashboard'],
              ['data-primary-color', '#059669', 'Widget colour (any hex code)'],
              ['data-position', 'bottom-right', 'bottom-right or bottom-left'],
              ['data-business-name', 'Support', 'Name shown in the chat header'],
            ].map(r => <tr key={r[0]}><td style={S.tdCode}>{r[0]}</td><td style={S.td}>{r[1]}</td><td style={S.td}>{r[2]}</td></tr>)}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>⚙️ Website Flow Builder tabs</h2>
        {[
          { t: '🔀 Flow Builder', d: 'Create and edit triggers/responses. Each trigger also has a "keyword aliases" field — comma-separated extra words that also match it.' },
          { t: '⚙️ Widget Settings', d: 'Set the greeting message (shown when chat opens) and the no-match message (shown when no trigger matches).' },
          { t: '🔌 Embed Code', d: 'Copy your embed snippet and view step-by-step install instructions including advanced options.' },
        ].map(item => (
          <div key={item.t} style={{ ...S.step, marginBottom: '8px' }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', minWidth: '160px' }}>{item.t}</div>
            <p style={{ ...S.p, margin: 0 }}>{item.d}</p>
          </div>
        ))}
      </div>
    </>
  );
}

function WhatsAppBotDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>💬 WhatsApp bot setup</h2>
        <p style={S.p}>When you create a WhatsApp bot, we submit your phone number to Meta for WhatsApp Business API access. Here's what you need:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {[
            '✅ A phone number NOT already on personal WhatsApp',
            '✅ A business name and address (for Meta verification)',
            '✅ A business email (for setup updates)',
            '✅ 1–3 business days for Meta approval',
          ].map(r => <div key={r} style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{r}</div>)}
        </div>
        <div style={S.tip}>💡 Use a dedicated SIM card or landline for your WhatsApp bot number. Once registered to the Business API, it cannot be used on the WhatsApp consumer app.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔗 Webhook setup (after approval)</h2>
        <p style={S.p}>Once your number is approved, you need to connect it in the Meta App Dashboard. Here's how:</p>
        {[
          { n: '1', t: 'Open Bot Settings', d: 'In the dashboard, select your WhatsApp bot and click Settings.' as React.ReactNode },
          { n: '2', t: 'Copy the Webhook URL', d: 'The settings modal shows your unique Webhook URL and Verify Token.' },
          { n: '3', t: 'Go to your Meta App Dashboard', d: <>Open <ExtLink href="https://developers.facebook.com/apps">developers.facebook.com/apps</ExtLink> → your App → WhatsApp → Configuration. (Full guide: <ExtLink href="https://developers.facebook.com/docs/whatsapp/cloud-api/guides/set-up-webhooks/">Meta's webhook setup docs</ExtLink>.)</> },
          { n: '4', t: 'Add the webhook', d: 'Paste the Webhook URL, enter the Verify Token (shown as BusinessName_<year> in Bot Settings — copy it exactly), and subscribe to the "messages" field.' },
          { n: '5', t: 'Save your credentials', d: 'Back in Bot Settings, enter your Phone Number ID and Access Token from the Meta Dashboard.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><div style={{ ...S.p, margin: 0 }}>{s.d}</div></div>
          </div>
        ))}
        <div style={S.warning}>
          ⚠️ The token you get from the App Dashboard's WhatsApp quick-start ("Temporary access token") expires in <strong>24 hours</strong> — fine for testing, not for production. For a token that doesn't expire, create a <strong>System User</strong> in Business Settings and generate a permanent token for it: <ExtLink href="https://business.facebook.com/settings/system-users">business.facebook.com/settings/system-users</ExtLink> → Add → assign your WhatsApp app → Generate New Token. Full guide: <ExtLink href="https://developers.facebook.com/docs/business-management-apis/system-users/install-apps-and-generate-tokens/">Meta's System User token docs</ExtLink>.
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📝 WhatsApp markdown</h2>
        <p style={S.p}>You can use WhatsApp's built-in formatting in your Text responses:</p>
        <pre style={S.code}>{`*bold text*
_italic text_
~strikethrough~
\`\`\`monospace\`\`\`

Line breaks: just press Enter in the text field`}</pre>
      </div>
    </>
  );
}

function FacebookBotDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>📘 Facebook bot setup</h2>
        <p style={S.p}>The Facebook bot replies to messages sent to your Facebook business page. You connect it using your Page ID and Page Access Token.</p>
        <div style={{ marginBottom: '16px' }}>
          {['✅ Admin access to the Facebook Page', '✅ Page Access Token with pages_messaging permission', '✅ Page must be published (not draft)'].map(r => (
            <div key={r} style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '6px' }}>{r}</div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔑 Getting your credentials</h2>
        {[
          { n: '1', t: 'Go to the Meta App Dashboard', d: <>Open <ExtLink href="https://developers.facebook.com/apps">developers.facebook.com/apps</ExtLink> and log in with the Facebook account that manages your page.</> as React.ReactNode },
          { n: '2', t: 'Create or open your app', d: 'Create a Business app if you don\'t have one. Add the Messenger product.' },
          { n: '3', t: 'Find your Page ID', d: 'Go to your Facebook Page → About → scroll to find Page ID (a long number).' },
          { n: '4', t: 'Generate a permanent Page Access Token', d: <>Page Access Tokens from the quick-start panel don't auto-expire, but the safest production path is a <strong>System User</strong> token: <ExtLink href="https://business.facebook.com/settings/system-users">Business Settings → System Users</ExtLink> → Add → assign your app and Page → Generate New Token with pages_messaging permission. Guide: <ExtLink href="https://developers.facebook.com/docs/business-management-apis/system-users/install-apps-and-generate-tokens/">Meta's System User docs</ExtLink>.</> },
          { n: '5', t: 'Enter in Bot Settings', d: 'When creating or editing your Facebook bot, paste both the Page ID and the Page Access Token.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><div style={{ ...S.p, margin: 0 }}>{s.d}</div></div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>⚙️ Facebook Flow Builder tabs</h2>
        {[
          { t: '💬 Auto-replies', d: 'Keyword triggers with text responses and quick reply buttons (max 3, 20 char limit per button).' },
          { t: '☰ Persistent Menu', d: 'Up to 3 always-visible menu items in the chat interface. Each can trigger a reply or open a URL.' },
          { t: '⚙️ Settings', d: 'Set the get-started message shown to new users who click "Get Started".' },
        ].map(item => (
          <div key={item.t} style={{ ...S.step, marginBottom: '8px' }}>
            <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', minWidth: '180px' }}>{item.t}</div>
            <p style={{ ...S.p, margin: 0 }}>{item.d}</p>
          </div>
        ))}
      </div>
    </>
  );
}

function VoiceBotDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🎙️ Voice bot overview</h2>
        <p style={S.p}>The voice bot is an AI voice assistant embedded on your website. Visitors can speak to it and it responds with synthesised speech. It's powered by text-to-speech and runs the flow you build in the Voice Flow Builder.</p>
        <div style={S.tip}>💡 Write your voice scripts as natural spoken sentences. Avoid bullet points or markdown — it'll be read aloud exactly as written.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔀 Voice Flow Builder — step types</h2>
        {[
          { icon: '🔊', n: 'Say', d: 'The bot speaks this text. Use {{variableName}} to insert collected answers. Good for information delivery.' },
          { icon: '❓', n: 'Ask', d: 'The bot asks a question and waits for a spoken answer. The answer is saved as a variable you can use in Say steps.' },
          { icon: '🔀', n: 'Branch', d: 'IVR menu. Bot reads the options, listens, then routes to a different trigger based on what the caller says. Each branch maps a match word to a trigger name.' },
          { icon: '📞', n: 'Transfer', d: 'Bot says a message then transfers the caller to a phone number.' },
          { icon: '📴', n: 'End call', d: 'Bot says a goodbye message and hangs up.' },
        ].map(item => (
          <div key={item.n} style={{ display: 'flex', gap: '12px', padding: '12px', background: 'var(--bg-sidebar)', borderRadius: '9px', border: '1px solid var(--bg-element)', marginBottom: '8px' }}>
            <span style={{ fontSize: '20px', flexShrink: 0 }}>{item.icon}</span>
            <div>
              <div style={S.h3}>{item.n}</div>
              <p style={{ ...S.p, margin: 0 }}>{item.d}</p>
            </div>
          </div>
        ))}
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🌐 Language & voice settings</h2>
        <p style={S.p}>In the Voice Flow Builder top bar, you can select:</p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
          {[['Language', 'English (US/UK/ZW), Shona, Ndebele'], ['Voice type', 'Woman or Man']].map(([k, v]) => (
            <div key={k} style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--bg-element)', borderRadius: '8px', padding: '12px', minWidth: '200px' }}>
              <div style={{ color: 'var(--text-muted)', fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>{k}</div>
              <div style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>{v}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>💡 Example voice flow</h2>
        <pre style={S.code}>{`Trigger: "welcome"
  Step 1 — Branch:
    "Thank you for calling. For sales, say Sales.
     For support, say Support. For our hours, say Hours."
    → "sales" → trigger: sales
    → "support" → trigger: support
    → "hours" → trigger: hours

Trigger: "sales"
  Step 1 — Say: "Our packages start from $15 per month."
  Step 2 — Ask: "What is your name?" → saves as {{caller_name}}
  Step 3 — Say: "Thank you {{caller_name}}. A sales agent will call you back."
  Step 4 — End call: "Have a great day! Goodbye."`}</pre>
      </div>
    </>
  );
}

function AIBotDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🤖 AI Bot overview</h2>
        <p style={S.p}>An AI Bot replaces fixed triggers/responses with an AI model (Groq) that answers customer questions in natural language, using only a knowledge base you write. Available on WhatsApp, Website, or Facebook — pick the channel when you create the bot.</p>
        <div style={S.warning}>⚠️ AI Bots require the Business plan or above. They don't use the Flow Builder — there's nothing to build, just a knowledge base to fill in.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>⚙️ Setup</h2>
        {[
          { n: '1', t: 'Create the bot', d: 'In + New Bot, choose "AI Bot", then pick a channel (WhatsApp, Website, or Facebook).' },
          { n: '2', t: 'Get a free Groq API key', d: 'Groq hosts the AI model your bot runs on. Sign up free (no credit card) and create a key.' },
          { n: '3', t: 'Paste the key into the bot', d: 'During creation (or later in Bot Settings), paste your Groq API key into the "AI API Key" field.' },
          { n: '4', t: 'Add channel credentials', d: 'WhatsApp/Facebook AI bots need the same Phone Number ID / Page credentials as regular bots — see those sections for the Meta setup steps. Website AI bots just need your business email.' },
          { n: '5', t: 'Write your Knowledge Base', d: 'In Bot Settings, paste everything the bot should know — pricing, FAQs, policies, hours. The AI only answers from this text; it will never invent facts.' },
          { n: '6', t: 'Pick a Tone', d: 'Choose how the bot should sound — see Tone presets below.' },
        ].map(s => (
          <div key={s.n} style={S.step}>
            <div style={S.stepNum}>{s.n}</div>
            <div><div style={S.h3}>{s.t}</div><p style={{ ...S.p, margin: 0 }}>{s.d}</p></div>
          </div>
        ))}
        <div style={S.tip}>💡 Get your free Groq API key at <ExtLink href="https://console.groq.com/keys">console.groq.com/keys</ExtLink>. The free tier is enough for most small businesses — see Groq's <ExtLink href="https://console.groq.com/docs/rate-limits">rate limits</ExtLink> if you're sending high volumes.</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🎭 Tone presets</h2>
        <p style={S.p}>Set in Bot Settings → Tone. Changes how the bot phrases every reply — it doesn't change what facts it's allowed to use.</p>
        <table style={S.table}>
          <thead><tr>{['Tone', 'Behaviour'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['Friendly & Casual', 'Default. Warm, conversational — like chatting with a helpful friend. No sales pressure.'],
              ['Marketer', 'Sells the value and benefit of what you offer. Ends replies with a relevant follow-up question.'],
              ['Sales-driven', 'Never lets a reply dead-end — always offers a next step (quote, booking, contact details) and asks qualifying questions.'],
              ['Straight to the Point', 'Brief and direct. No pitch, no follow-up question, just the answer.'],
            ].map(([t, d]) => <tr key={t}><td style={S.td}><strong style={{ color: 'var(--text-primary)' }}>{t}</strong></td><td style={S.td}>{d}</td></tr>)}
          </tbody>
        </table>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📚 Writing a good Knowledge Base</h2>
        <p style={S.p}>The AI only knows what's in this text box — if it's not there, the bot tells the customer to contact you directly rather than guessing.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
          {[
            'Use clear ALL-CAPS section headers (e.g. "PRICING", "OUTDOOR ADVERTISING", "FAQ") — large knowledge bases are automatically split by these headers so the bot can pull in the most relevant section for each question.',
            'Be specific: include exact prices, locations, hours, policies — not vague summaries.',
            'Write it like a reference doc, not a sales page. The Tone setting handles how it\'s presented.',
            'Large knowledge bases (8,000+ characters) are fine — only the most relevant sections are sent per reply, so keep related facts grouped under the same header.',
          ].map(r => <div key={r} style={{ color: 'var(--text-secondary)', fontSize: '13px', lineHeight: '1.6' }}>• {r}</div>)}
        </div>
        <pre style={S.code}>{`================================================================
PRICING
================================================================
Static Billboard — Harare CBD: $450-$500/month
Static Billboard — Highway/Tollgate: $450-$500/month

================================================================
FAQ
================================================================
Q: Do you offer discounts for long-term bookings?
A: Yes, contact our team for multi-month packages.`}</pre>
        <div style={S.tip}>💡 The bot's "Custom System Instructions" field (also in Bot Settings) is for behaviour rules that aren't facts — e.g. "Always reply in Shona" or "Never discuss competitor pricing."</div>
      </div>
    </>
  );
}

function AdvancedWA() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>📡 Dynamic Lists</h2>
        <p style={S.p}>A Dynamic List fetches items from your API and shows them as a WhatsApp list. Perfect for product catalogues, service menus, or any data that changes often.</p>
        <table style={S.table}>
          <thead><tr>{['Field', 'Example', 'Description'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['API URL', 'https://api.yoursite.com/products', 'Endpoint that returns array of items'],
              ['API Method', 'GET', 'GET or POST'],
              ['Title path', 'name', 'JSON path to display text (e.g. name → item.name)'],
              ['Payload path', 'id', 'JSON path to unique ID sent when user selects'],
              ['List button title', 'Our Products', 'Heading above the list'],
            ].map(r => <tr key={r[0]}><td style={S.tdCode}>{r[0]}</td><td style={S.tdCode}>{r[1]}</td><td style={S.td}>{r[2]}</td></tr>)}
          </tbody>
        </table>
        <pre style={{ ...S.code, marginTop: '14px' }}>{`// Your API must return JSON like:
{
  "data": [
    { "id": "prod_001", "name": "Basic Website — $500" },
    { "id": "prod_002", "name": "E-commerce Store — $1,200" }
  ]
}
// Title path: name   (shows "Basic Website — $500")
// Payload path: id   (sends "prod_001" when user selects)`}</pre>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>🔗 Dynamic Responses</h2>
        <p style={S.p}>When a user selects from a Dynamic List, a Dynamic Response fetches and displays full details about that item — image, title, price, description, and action buttons.</p>
        <pre style={S.code}>{`// Your detail API returns:
{
  "data": {
    "id": "prod_001",
    "name": "Basic Website",
    "price": "$500",
    "category": "Web Development",
    "description": "5-page site with contact form and Google Maps.",
    "image_url": "https://yoursite.com/images/basic.jpg"
  }
}

// Configure:
// API URL: https://api.yoursite.com/product/{{id}}
// Title path: data.name
// Meta path: data.price    Meta label: Price
// Description path: data.description
// Image path: data.image_url (optional)
// Button 1: "Order Now" → payload: order_{{id}}`}</pre>
        <div style={S.tip}>{`💡 Use {{id}} in the API URL and button payloads. It gets replaced with the actual ID the user selected from the list.`}</div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📄 Automatic Pagination</h2>
        <p style={S.p}>WhatsApp lists are capped at 10 rows. When your API returns more than 9 items, the bot paginates automatically — no configuration required.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '14px' }}>
          {[
            { n: '1', t: 'First page', d: 'Shows items 1–9 plus a "➡️ More (N remaining)" row.' },
            { n: '2', t: 'User taps "More"', d: 'Next 9 items load as a new list. Continues until all items have been shown.' },
            { n: '3', t: 'Item tap', d: 'When the customer selects any item, full details are shown automatically from the cached result — no extra API call.' },
          ].map(s => (
            <div key={s.n} style={S.step}>
              <div style={S.stepNum}>{s.n}</div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '13px', marginBottom: '2px' }}>{s.t}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
        <div style={S.tip}>💡 Item details (name, format, location, price, availability) are displayed directly from the cached list data. For database-backed flows the detail card is built automatically — no "Dynamic Response" config is needed.</div>
      </div>
    </>
  );
}

function OrderHandlerDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🛒 Order Handler</h2>
        <p style={S.p}>The Order Handler is a complete order-processing flow. It collects customer details, asks for quantity, calculates totals, and then takes one of four actions.</p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '10px', marginBottom: '16px' }}>
          {[
            { icon: '💰', label: 'Payment link', desc: 'Generates a link with order details as URL params.' },
            { icon: '📲', label: 'Notify admin', desc: 'Sends order details to your WhatsApp number.' },
            { icon: '🌐', label: 'Call API', desc: 'Posts order data to your backend system.' },
            { icon: '💬', label: 'Custom message', desc: 'Sends a custom confirmation message.' },
          ].map(a => (
            <div key={a.label} style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--bg-element)', borderRadius: '9px', padding: '14px' }}>
              <div style={{ fontSize: '20px', marginBottom: '6px' }}>{a.icon}</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '13px', marginBottom: '3px' }}>{a.label}</div>
              <div style={{ color: 'var(--text-faint)', fontSize: '11px' }}>{a.desc}</div>
            </div>
          ))}
        </div>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📦 Available variables</h2>
        <pre style={S.code}>{`{{id}}               — Product ID from the Dynamic List
{{price}}            — Product price
{{quantity}}         — Quantity ordered (or months for bookings)
{{total}}            — price × quantity (auto-calculated)
{{customer_name}}    — Collected name
{{customer_phone}}   — Collected phone
{{customer_email}}   — Collected email
{{customer_address}} — Collected delivery address

// Payment URL example:
https://pay.example.com/checkout?id={{id}}&amount={{total}}&name={{customer_name}}`}</pre>
      </div>

      <div style={S.card}>
        <h2 style={S.h2}>📅 Duration / Months question (Ask Quantity)</h2>
        <p style={S.p}>For booking or advertising flows, enable <strong>Ask Quantity</strong> to add a duration question after name and phone. The customer's answer is stored as <code>{'{{quantity}}'}</code> and included in the booking record.</p>
        <table style={S.table}>
          <thead><tr>{['Field', 'Example value', 'Notes'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['Ask quantity', '✓ enabled', 'Turns on the extra question after phone collection'],
              ['Quantity label', 'How many months would you like to advertise? (e.g. 1, 3, 6 or 12)', 'The exact question shown to the customer'],
              ['Action', 'admin_notification', 'Saves booking to Firebase — no admin WhatsApp number required'],
              ['Success message', '✅ Booking received! Our team will call {{customer_phone}} within 2 hours.', 'Shown to the customer. Use {{quantity}}, {{customer_name}}, {{customer_phone}} etc.'],
            ].map(r => <tr key={r[0]}><td style={S.tdCode}>{r[0]}</td><td style={{ ...S.td, fontSize: '12px' }}>{r[1]}</td><td style={S.td}>{r[2]}</td></tr>)}
          </tbody>
        </table>
        <pre style={{ ...S.code, marginTop: '14px' }}>{`// Example success message with all booking variables:
✅ *Booking Enquiry Received!*

Thank you, *{{customer_name}}*! You have requested *{{quantity}} month(s)*.

Our team will call *{{customer_phone}}* within *2 business hours* to confirm.

📞 +263 78 000 0000
📧 info@yourcompany.com`}</pre>
        <div style={{ ...S.tip, marginTop: '14px' }}>💡 The booking is saved to the <strong>Bookings tab</strong> automatically when the customer completes the form. You do not need a Webhook response — <code>admin_notification</code> handles the save. See the <strong>Inventory & Bookings</strong> section for the full availability system.</div>
      </div>
    </>
  );
}

function WebhooksDoc() {
  return (
    <>
      <div style={S.card}>
        <h2 style={S.h2}>🌐 Webhook response type</h2>
        <p style={S.p}>The Webhook response calls any external API when triggered. Use it to check order status, fetch data, or trigger actions in your system.</p>
        <table style={S.table}>
          <thead><tr>{['Field', 'Description'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['API URL', 'The endpoint to call. Use {{message}} or {{phone}} to pass user data.'],
              ['Method', 'GET or POST'],
              ['POST Body', 'JSON body for POST requests. Use {{message}} and {{phone}} as variables.'],
              ['Response path', 'JSON path to extract from response (e.g. data.status)'],
              ['Success message', 'Message to show if the call succeeds'],
              ['Error message', 'Message to show if the call fails'],
            ].map(r => <tr key={r[0]}><td style={S.tdCode}>{r[0]}</td><td style={S.td}>{r[1]}</td></tr>)}
          </tbody>
        </table>
        <pre style={{ ...S.code, marginTop: '14px' }}>{`// Example: Check order status
API URL: https://api.yoursite.com/order/{{message}}
Method: GET
Response path: data.status
Success message: "Your order status is: {{response}}"

// Example: POST with authentication
API URL: https://api.yoursite.com/lead
Method: POST
Headers: [{"key":"Authorization","value":"Bearer YOUR_TOKEN"}]
Body: {"phone":"{{phone}}","message":"{{message}}"}`}</pre>
      </div>
    </>
  );
}

function AnalyticsDoc() {
  return (
    <div style={S.card}>
      <h2 style={S.h2}>📈 Analytics</h2>
      <p style={S.p}>Each bot has three analytics tabs: Overview, Conversations, and Reports.</p>
      {[
        { t: 'Overview', items: ['Stats cards (messages, customers, auto-handled %, savings)', '7-day message volume bar chart', 'Last 5 conversations preview'] },
        { t: 'Conversations', items: ['Full conversation history', 'Filter by phone number and date range', 'Export all conversations as CSV'] },
        { t: 'Reports', items: ['Average messages per day', 'Best single day', 'Messages per unique user', 'Download daily report as CSV'] },
      ].map(tab => (
        <div key={tab.t} style={{ marginBottom: '16px' }}>
          <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '14px', marginBottom: '8px' }}>{tab.t}</div>
          <ul style={{ color: 'var(--text-muted)', fontSize: '13px', lineHeight: '2', paddingLeft: '20px' }}>
            {tab.items.map(i => <li key={i}>{i}</li>)}
          </ul>
        </div>
      ))}
      <div style={S.tip}>💡 Use the CSV export to open data in Excel or Google Sheets for deeper analysis or to share with your team.</div>
    </div>
  );
}

function TroubleshootingDoc() {
  const issues = [
    { prob: 'Bot doesn\'t respond at all (WhatsApp)', sol: 'Check that your webhook is verified in Meta Dashboard. Go to Bot Settings → copy the Webhook URL → paste it in Meta Console → WhatsApp → Configuration → Webhook. Make sure you\'ve subscribed to "messages".' },
    { prob: '"Invalid access token" error', sol: 'If you used the quick-start "Temporary access token," it expires after 24 hours — that\'s expected. For a token that doesn\'t expire, generate a permanent token via a System User in Business Settings → System Users, then paste it into Bot Settings. See the WhatsApp Bot page for the full link and steps.' },
    { prob: 'Webhook verification fails', sol: 'Create the bot in ZimChat first, then copy the webhook URL. The bot must exist before you can verify. Also check your Verify Token exactly matches the BusinessName_<year> value shown in Bot Settings — copy it rather than retyping it.' },
    { prob: 'AI bot keeps saying "I don\'t have that information"', sol: 'The AI only answers from the Knowledge Base text in Bot Settings — it never guesses. Add the missing fact there. If your knowledge base is large, make sure it\'s organised under clear headers so the relevant section gets picked up.' },
    { prob: 'Bot responds with the wrong trigger', sol: 'Triggers match ANY message containing that word. Make triggers specific (e.g. "opening_hours" not "hours" which appears in many sentences). Longer, more specific triggers are safer.' },
    { prob: 'Images not sending', sol: 'Image URL must be HTTPS and publicly accessible. Test by opening the URL in your browser — if it shows the image without login, it works.' },
    { prob: 'Dynamic List shows no items', sol: 'Test your API URL directly in a browser. Make sure it returns JSON data. Check the "Title path" and "Payload path" exactly match your JSON keys.' },
    { prob: '{{variables}} not being replaced', sol: 'Variables are only available after an Ask response stores them. Make sure the variable name in the Ask field exactly matches what you\'re using in {{}} — they are case-sensitive.' },
    { prob: 'Website widget not appearing', sol: 'Make sure the script tag is placed before the closing </body> tag. Check that your domain is not in the restricted list (Bot Settings → Allowed websites). Open browser console for errors.' },
    { prob: 'Template seeding fails (PERMISSION_DENIED)', sol: 'Add the templates node to your Firebase Realtime Database rules: "templates": {".read": "auth != null", ".write": "auth != null"}' },
    { prob: 'Facebook bot not replying', sol: 'Make sure you\'ve set up a webhook in Meta Console pointing to your ZimChat backend. The Page Access Token must have pages_messaging permission.' },
    { prob: '"Duplicated row id" error from WhatsApp', sol: 'This happens when two items in your database have the same id value, causing duplicate list row IDs. The system now uses position-based IDs automatically. If you see this error, redeploy the server or check that you are on the latest server version.' },
    { prob: 'Dynamic list shows "Item details not configured"', sol: 'This means the flow has no dynamicResponseConfig for item details. For database-backed flows, the bot builds item details automatically from the cached list — no extra configuration needed. Make sure you are using the latest flow version (v3+) imported through the Flow Builder.' },
    { prob: 'Customer can book a space that is already taken', sol: 'When a booking is submitted, the slot is locked automatically. If a second customer can still book it, the first booking may have been cancelled. Check the Bookings tab — if the status is "cancelled", cancel releases the lock. Re-confirm or re-create the booking to re-lock it.' },
    { prob: 'CSV import fails with "headers must match field names"', sol: 'The CSV header row must contain exactly the same column names as the table fields — no extra columns, no misspellings. Check for a "category" or other extra column. Export the table definition first to see the expected headers, then match them exactly in your CSV.' },
    { prob: 'Booking shows no product name in Bookings tab', sol: 'The item was selected before the server update that saves product info. New bookings will include the product. Old bookings only show the customer variables. Delete old test bookings and create a fresh enquiry to see the full product info.' },
  ];
  return (
    <div style={S.card}>
      <h2 style={S.h2}>🔧 Common issues & fixes</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
        {issues.map(item => (
          <div key={item.prob} style={{ borderRadius: '10px', overflow: 'hidden', border: '1px solid var(--bg-element)' }}>
            <div style={{ background: '#f59e0b0d', borderBottom: '1px solid #f59e0b20', padding: '10px 14px' }}>
              <span style={{ color: '#f59e0b', fontWeight: '600', fontSize: '13px' }}>⚠️ {item.prob}</span>
            </div>
            <div style={{ padding: '10px 14px', background: '#10b9810d' }}>
              <span style={{ color: '#10b981', fontSize: '13px' }}>✅ {item.sol}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function FAQDoc() {
  const faqs = [
    { q: 'Do I need coding skills?', a: 'No. The Flow Builder is visual — you type, click, and configure. No code required for any bot type.' },
    { q: 'Can I have multiple bots?', a: 'Yes. Free: 1 website bot. Starter: 1 bot (any channel). Business: 3 bots. Agency: 10 bots. Enterprise: unlimited. Bots can be different types.' },
    { q: 'What\'s the difference between a regular bot and an AI Bot?', a: 'Regular bots match keyword triggers you set up in the Flow Builder. AI Bots (Business plan+) use a Groq-powered AI that answers naturally from a knowledge base you write — no triggers to configure, but it only knows what\'s in that knowledge base.' },
    { q: 'Can I use my personal WhatsApp number?', a: 'No. Each phone number can only have one WhatsApp account. You\'ll need a separate SIM or landline for the bot.' },
    { q: 'How long does WhatsApp setup take?', a: '1–3 business days for Meta to verify and approve your number.' },
    { q: 'Can customers talk to a human?', a: 'Yes. Use the "Hand off to human" response type (website bot) or add a trigger that tells customers to call/email you directly.' },
    { q: 'Can I change my bot later?', a: 'Yes. The Flow Builder is live-editable. Save changes and they take effect immediately.' },
    { q: 'What happens when a message doesn\'t match any trigger?', a: 'For WhatsApp bots, the bot sends nothing (or you can add a fallback trigger). For website bots, the "offline/no-match message" is shown.' },
    { q: 'Can I export conversation data?', a: 'Yes. Go to the Conversations tab → Export CSV. You can also export daily report data from the Reports tab.' },
    { q: 'Is my data secure?', a: 'Yes. Data is stored in Firebase with authentication rules. Access tokens are stored encrypted. We never share your conversation data.' },
    { q: `How do I contact ${companyName} support?`, a: `Email ${companyEmail} or use the Support link in the sidebar. Business and Agency plans get priority response times.` },
    { q: 'How do I release a booked slot so someone else can book it?', a: 'Go to your bot → Bookings tab → find the booking → click the row to expand it → click "✕ Cancel". This immediately removes the lock so other customers will see the item as available again.' },
    { q: 'Can two customers book the same space at the same time?', a: 'No. As soon as the first customer completes the enquiry form, the slot is locked. Any other customer who taps the same item will see "⚠️ This space is currently unavailable" and be offered alternative options or a waitlist contact.' },
    { q: 'Where do bookings go? How do I see them?', a: 'All bookings appear in the Bookings tab inside your bot dashboard. Each booking shows the customer\'s name, the space they enquired about, phone number, months requested, and status. You can filter by Pending / Confirmed / Completed / Cancelled.' },
    { q: 'What does the quantity / months variable do?', a: 'When "Ask Quantity" is enabled on the Order Handler, the bot asks the customer how many months they want after collecting their name and phone. Their answer is saved as {{quantity}} and appears in the Bookings tab alongside the booking details.' },
    { q: 'My Dynamic List has more than 9 items — will it all show?', a: 'Yes. The system paginates automatically. Customers see 9 items per page, with a "➡️ More (N remaining)" option to load the next page. This works for any number of items.' },
  ];
  return (
    <div style={S.card}>
      <h2 style={S.h2}>❓ Frequently asked questions</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
        {faqs.map((faq, i) => (
          <div key={i} style={{ borderBottom: i < faqs.length - 1 ? '1px solid var(--bg-element)' : 'none', paddingBottom: '16px', marginBottom: '16px' }}>
            <div style={{ color: '#818cf8', fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>❓ {faq.q}</div>
            <p style={{ ...S.p, margin: 0 }}>{faq.a}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function BookingsSystemDoc() {
  return (
    <>
      {/* Overview */}
      <div style={S.card}>
        <h2 style={S.h2}>📅 Inventory & Bookings System</h2>
        <p style={S.p}>
          Connect a database of products or spaces to your bot. Customers browse items in WhatsApp,
          tap to see details, and submit an enquiry in seconds. The system locks booked items immediately
          so no two customers can claim the same slot.
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap', margin: '16px 0' }}>
          {[
            { icon: '🗄️', label: 'Database', desc: 'CSV or manual rows' },
            { icon: '📡', label: 'Dynamic List', desc: 'Browse with pagination' },
            { icon: '🛒', label: 'Order Handler', desc: 'Name → phone → months' },
            { icon: '📅', label: 'Bookings Tab', desc: 'Confirm / Cancel / Release' },
          ].map((step, i, arr) => (
            <React.Fragment key={step.label}>
              <div style={{
                background: 'var(--bg-sidebar)', border: '1px solid var(--bg-element)',
                borderRadius: '10px', padding: '10px 12px', textAlign: 'center', minWidth: '90px',
              }}>
                <div style={{ fontSize: '20px', marginBottom: '4px' }}>{step.icon}</div>
                <div style={{ fontWeight: '600', fontSize: '11px', color: 'var(--text-primary)' }}>{step.label}</div>
                <div style={{ fontSize: '10px', color: 'var(--text-faint)', marginTop: '2px' }}>{step.desc}</div>
              </div>
              {i < arr.length - 1 && <div style={{ color: 'var(--text-faint)', fontSize: '18px' }}>→</div>}
            </React.Fragment>
          ))}
        </div>
        <div style={S.success}>✅ Once configured, everything is automatic — bookings saved, slots locked, confirmation sent to the customer instantly.</div>
      </div>

      {/* Step 1: Database */}
      <div style={S.card}>
        <h2 style={S.h2}>🗄️ Step 1 — Set up the Database</h2>
        <p style={S.p}>Go to your bot's <strong>Database</strong> tab. Create a table and import your inventory as a CSV file, or add rows manually.</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {[
            { n: '1', t: 'Create a new table', d: 'Click "New Table", give it a name (e.g. inventory), and define your columns. Use lowercase_underscore field names.' },
            { n: '2', t: 'Import CSV', d: 'Click "Import CSV". The CSV header row must exactly match the table field names — no extra columns, no misspellings.' },
            { n: '3', t: 'Verify the import', d: 'Check the row count and spot-check a few records. The API endpoint is live immediately after import.' },
          ].map(s => (
            <div key={s.n} style={S.step}>
              <div style={S.stepNum}>{s.n}</div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '13px', marginBottom: '2px' }}>{s.t}</div>
                <div style={{ color: 'var(--text-muted)', fontSize: '12px' }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Mock database table */}
        <div style={{ border: '1px solid var(--bg-element)', borderRadius: '10px', overflow: 'hidden' }}>
          <div style={{ padding: '8px 14px', background: '#5B4FE815', borderBottom: '1px solid var(--bg-element)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontWeight: '700', fontSize: '12px', color: '#818cf8' }}>📊 inventory</span>
            <span style={{ fontSize: '11px', color: 'var(--text-faint)' }}>99 rows · 9 columns</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ ...S.table, minWidth: '580px' }}>
              <thead>
                <tr>{['id', 'name', 'format', 'location', 'price_monthly', 'availability'].map(h => (
                  <th key={h} style={{ ...S.th, background: 'var(--bg-sidebar)' }}>{h}</th>
                ))}</tr>
              </thead>
              <tbody>
                {[
                  ['1', 'Samora/Julius Nyerere', 'Static Billboard', 'Samora Machel Ave', '$450', 'Available'],
                  ['2', 'Simon Mazorodze/Chitungwiza', 'Static Billboard', 'Simon Mazorodze Rd', '$480', 'Available'],
                  ['3', 'The Herald — Front Page', 'Print Newspaper', 'Harare', '$1,200', 'Available'],
                  ['4', 'Facebook — Page Post', 'Social Media', 'National', '$350', 'Available'],
                ].map((row, i) => (
                  <tr key={i}>{row.map((cell, j) => (
                    <td key={j} style={j === 0 || j === 4 || j === 5 ? S.tdCode : S.td}>{cell}</td>
                  ))}</tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <div style={{ ...S.tip, marginTop: '14px' }}>
          💡 Recommended columns: <code>id</code> (unique, required), <code>name</code> (display title, required), <code>format</code>, <code>location</code>, <code>city</code>, <code>size</code>, <code>price_monthly</code>, <code>availability</code>, <code>description</code>. The bot auto-formats whichever fields are present.
        </div>
      </div>

      {/* Step 2: Flow builder */}
      <div style={S.card}>
        <h2 style={S.h2}>🔀 Step 2 — Build the Catalog Flow</h2>
        <p style={S.p}>In the Flow Builder, add a trigger and a Dynamic List response pointing to your database table.</p>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ border: '1.5px solid #5B4FE8', borderRadius: '10px', overflow: 'hidden', marginBottom: 0 }}>
            <div style={{ background: '#5B4FE815', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>⚡</span>
              <span style={{ fontWeight: '700', fontSize: '11px', color: '#818cf8', textTransform: 'uppercase' as const, letterSpacing: '.05em' }}>Trigger</span>
            </div>
            <div style={{ padding: '10px 14px' }}>
              <div style={{ fontSize: '12px', color: 'var(--text-faint)', marginBottom: '4px' }}>Message contains:</div>
              <code style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: '600' }}>browse spaces</code>
              <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginTop: '4px' }}>Add more: "billboard", "advertising", "book space", "catalogue"</div>
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', height: '20px', alignItems: 'center' }}>
            <div style={{ width: 2, height: '100%', background: 'var(--bg-element)' }} />
          </div>
          <div style={{ border: '1px solid var(--bg-element)', borderRadius: '10px', overflow: 'hidden', marginBottom: 0 }}>
            <div style={{ background: 'var(--bg-sidebar)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>📡</span>
              <span style={{ fontWeight: '700', fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase' as const, letterSpacing: '.05em' }}>Response — Dynamic List</span>
            </div>
            <div style={{ padding: '10px 14px' }}>
              {[
                ['API URL', 'https://server.autopilot.co.zw/api/db/query/{botId}/inventory?field=format&search=Static+Billboard'],
                ['API Method', 'GET'],
                ['Title path', 'name'],
                ['List button title', '📋 View Locations'],
              ].map(([k, v]) => (
                <div key={k} style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: '8px', fontSize: '12px', marginBottom: '5px' }}>
                  <span style={{ color: 'var(--text-faint)', fontWeight: '600' }}>{k}</span>
                  <code style={{ color: '#818cf8', wordBreak: 'break-all' as const }}>{v}</code>
                </div>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', height: '20px', alignItems: 'center' }}>
            <div style={{ width: 2, height: '100%', background: 'var(--bg-element)' }} />
          </div>
          <div style={{ border: '1px solid #10b98130', borderRadius: '10px', padding: '10px 14px', background: '#10b9810d' }}>
            <span style={{ fontSize: '12px', color: '#10b981' }}>✅ <strong>Automatic</strong>: When a customer taps an item, the bot shows full details (name, format, location, price, availability) plus "Enquire Now" and "Browse More" buttons — no extra config needed.</span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', height: '20px', alignItems: 'center' }}>
            <div style={{ width: 2, height: '100%', background: 'var(--bg-element)' }} />
          </div>
          <div style={{ border: '1px solid var(--bg-element)', borderRadius: '10px', overflow: 'hidden' }}>
            <div style={{ background: 'var(--bg-sidebar)', padding: '8px 14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span>🛒</span>
              <span style={{ fontWeight: '700', fontSize: '11px', color: 'var(--text-secondary)', textTransform: 'uppercase' as const, letterSpacing: '.05em' }}>Order Handler — triggered by "Enquire Now"</span>
            </div>
            <div style={{ padding: '10px 14px', fontSize: '12px', color: 'var(--text-muted)' }}>
              Collects customer name → phone number → months. See Step 3 for full configuration.
            </div>
          </div>
        </div>
        <pre style={{ ...S.code, marginTop: '14px' }}>{`// API URL formats for database queries:

// All items in the table (no filter):
https://server.autopilot.co.zw/api/db/query/{botId}/inventory

// Filter by format column:
https://server.autopilot.co.zw/api/db/query/{botId}/inventory?field=format&search=Static+Billboard

// Filter by city:
https://server.autopilot.co.zw/api/db/query/{botId}/inventory?field=city&search=Harare

// Replace {botId} with your actual bot ID (visible in Bot Settings)`}</pre>
      </div>

      {/* Step 3: Order Handler */}
      <div style={S.card}>
        <h2 style={S.h2}>🛒 Step 3 — Configure the Order Handler</h2>
        <p style={S.p}>The Order Handler is attached to any response (click the cart icon). For bookings, use this minimum configuration:</p>
        <table style={S.table}>
          <thead><tr>{['Setting', 'Value', 'Effect'].map(h => <th key={h} style={S.th}>{h}</th>)}</tr></thead>
          <tbody>
            {[
              ['Collect name', '✓ On', 'Asks "What is your name?" → saves as {{customer_name}}'],
              ['Collect phone', '✓ On', 'Asks "What is your phone number?" → saves as {{customer_phone}}'],
              ['Ask quantity', '✓ On', 'Adds a months question after phone collection → saves as {{quantity}}'],
              ['Quantity label', 'How many months would you like to advertise? (e.g. 1, 3, 6 or 12)', 'The exact question shown to the customer'],
              ['Action', 'admin_notification', 'Saves booking to Firebase and sends success message (no admin WhatsApp number needed)'],
              ['Success message', 'See template below', 'Confirmation shown to customer after submission'],
            ].map(r => <tr key={r[0]}><td style={S.tdCode}>{r[0]}</td><td style={{ ...S.td, fontSize: '12px' }}>{r[1]}</td><td style={S.td}>{r[2]}</td></tr>)}
          </tbody>
        </table>
        <pre style={{ ...S.code, marginTop: '14px' }}>{`// Recommended success message template:
✅ *Booking Enquiry Received!*

Thank you, *{{customer_name}}*! You have requested *{{quantity}} month(s)* of advertising.

Our team will call *{{customer_phone}}* within *2 business hours* to confirm availability and finalise your booking.

📞 +263 78 000 0000
📧 info@yourcompany.com

// Available variables:
// {{customer_name}}  — name entered
// {{customer_phone}} — phone entered
// {{quantity}}       — months entered
// {{id}}             — item ID from database
// {{price}}          — item price_monthly field`}</pre>
      </div>

      {/* WhatsApp experience */}
      <div style={S.card}>
        <h2 style={S.h2}>📱 The Customer Experience (WhatsApp)</h2>
        <p style={S.p}>The full conversation from trigger to confirmation:</p>
        <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* Phone mockup */}
          <div style={{
            width: '268px', background: '#ece5dd', borderRadius: '16px',
            overflow: 'hidden', boxShadow: '0 4px 24px rgba(0,0,0,.15)', flexShrink: 0,
          }}>
            <div style={{ background: '#075E54', padding: '12px 14px', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,.2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>🤖</div>
              <div>
                <div style={{ color: '#fff', fontSize: '13px', fontWeight: '600' }}>AdSpaces Bot</div>
                <div style={{ color: 'rgba(255,255,255,.7)', fontSize: '11px' }}>online</div>
              </div>
            </div>
            <div style={{ padding: '10px 8px', display: 'flex', flexDirection: 'column', gap: '5px' }}>
              {/* Customer trigger */}
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '12px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>browse spaces</div>
              {/* List */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', overflow: 'hidden', maxWidth: '84%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>
                <div style={{ padding: '6px 10px', fontSize: '11px', color: '#111', borderBottom: '1px solid #f0f0f0' }}>Here are our billboard locations 📋</div>
                {['Samora/Julius Nyerere', 'Simon Mazorodze/Chitungwiza', 'Borrowdale Rd — Highlands', '➡️ More (6 remaining)'].map((r, i) => (
                  <div key={i} style={{ padding: '5px 10px', fontSize: '11px', color: '#128C7E', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>{r}</div>
                ))}
                <div style={{ padding: '4px 10px', background: '#f7f7f7', fontSize: '10px', color: '#666', textAlign: 'center' as const }}>📋 View Locations</div>
              </div>
              {/* User selects */}
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>Samora/Julius Nyerere</div>
              {/* Item detail */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', overflow: 'hidden', maxWidth: '84%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>
                <div style={{ padding: '6px 10px', fontSize: '11px', color: '#111', lineHeight: '1.6', borderBottom: '1px solid #f0f0f0' }}>
                  <strong>Samora/Julius Nyerere</strong><br/>
                  📌 Static Billboard<br/>
                  💰 $450/month · ✅ Available
                </div>
                <div style={{ padding: '5px 10px', fontSize: '11px', color: '#128C7E', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>📋 Enquire Now</div>
                <div style={{ padding: '5px 10px', fontSize: '11px', color: '#128C7E', fontWeight: 500 }}>🗺️ Browse More</div>
              </div>
              {/* Enquire Now */}
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>📋 Enquire Now</div>
              {/* Name */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>What is your name?</div>
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>John Doe</div>
              {/* Phone */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>What is your phone number?</div>
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '72%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>+263 77 123 4567</div>
              {/* Months */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '80%', lineHeight: '1.5', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>How many months would you like to advertise? (e.g. 1, 3, 6 or 12)</div>
              <div style={{ alignSelf: 'flex-end', background: '#dcf8c6', borderRadius: '8px 0 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '40%', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>3</div>
              {/* Success */}
              <div style={{ alignSelf: 'flex-start', background: '#fff', borderRadius: '0 8px 8px 8px', padding: '6px 10px', fontSize: '11px', color: '#111', maxWidth: '90%', lineHeight: '1.6', boxShadow: '0 1px 1px rgba(0,0,0,.08)' }}>
                ✅ <strong>Booking Enquiry Received!</strong><br/><br/>
                Thank you, <strong>John Doe</strong>! You requested <strong>3 month(s)</strong>.<br/><br/>
                Our team will call <strong>+263 77 123 4567</strong> within 2 business hours.<br/><br/>
                📞 +263 78 000 0000
              </div>
            </div>
          </div>
          {/* Annotations */}
          <div style={{ flex: 1, minWidth: '180px', display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {[
              { n: '1', t: 'Trigger keyword', d: 'Any message matching your trigger launches the flow.' },
              { n: '2', t: 'Paginated list', d: 'Up to 9 items per page. "➡️ More" loads the next batch automatically.' },
              { n: '3', t: 'Auto item detail', d: 'Tapping an item shows full database details plus action buttons — no extra config.' },
              { n: '4', t: 'Unavailable items', d: 'If already booked, the customer sees "⚠️ Currently unavailable" with alternatives instead.' },
              { n: '5', t: 'Enquiry form', d: 'Three questions: name → phone → months. Fast and friction-free.' },
              { n: '6', t: 'Instant confirmation', d: 'Booking saved, slot locked, success message sent. Appears in Bookings tab immediately.' },
            ].map(note => (
              <div key={note.n} style={S.step}>
                <div style={S.stepNum}>{note.n}</div>
                <div>
                  <div style={{ fontWeight: '600', fontSize: '12px', color: 'var(--text-primary)', marginBottom: '2px' }}>{note.t}</div>
                  <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>{note.d}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bookings tab */}
      <div style={S.card}>
        <h2 style={S.h2}>📋 Managing Bookings (Bookings Tab)</h2>
        <p style={S.p}>Go to your bot → <strong>Bookings</strong> tab to see all enquiries, filter by status, and take action on each one.</p>
        {/* Mock bookings panel */}
        <div style={{ border: '1px solid var(--bg-element)', borderRadius: '12px', overflow: 'hidden', marginBottom: '16px' }}>
          <div style={{ padding: '10px 14px', background: 'var(--bg-sidebar)', borderBottom: '1px solid var(--bg-element)', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
            {[
              { label: 'All (3)', active: true },
              { label: 'Pending (1)', active: false },
              { label: 'Confirmed (1)', active: false },
              { label: 'Completed (1)', active: false },
            ].map(tab => (
              <span key={tab.label} style={{
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600,
                background: tab.active ? '#5B4FE8' : 'transparent',
                color: tab.active ? '#fff' : 'var(--text-faint)',
                border: `1.5px solid ${tab.active ? '#5B4FE8' : 'var(--bg-element)'}`,
              }}>{tab.label}</span>
            ))}
          </div>
          {[
            { name: 'John Doe', product: '📌 Samora/Julius Nyerere — Harare', detail: '+263 77 123 4567 · 3 month(s) · $450/mo', status: 'Pending', sc: '#f59e0b', acts: ['Confirm', 'Complete', 'Cancel'] },
            { name: 'Tendai Mpofu', product: '📌 Borrowdale Rd — Highlands', detail: '+263 71 987 6543 · 6 month(s) · $500/mo', status: 'Confirmed', sc: '#10b981', acts: ['Complete', 'Cancel'] },
            { name: 'Chido Enterprises', product: '📌 The Herald — Front Page', detail: '+263 77 456 7890 · 1 month(s) · $1,200/mo', status: 'Completed', sc: '#6b7280', acts: [] },
          ].map((b, i) => (
            <div key={i} style={{ padding: '10px 14px', borderBottom: '1px solid var(--bg-element)', display: 'flex', alignItems: 'center', gap: '10px' }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: b.sc, flexShrink: 0 }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--text-primary)' }}>{b.name}</div>
                <div style={{ fontSize: 11, color: 'var(--text-faint)', marginTop: 1 }}>{b.product} · {b.detail}</div>
              </div>
              <div style={{ display: 'flex', gap: 5, flexShrink: 0, flexWrap: 'wrap', alignItems: 'center' }}>
                <span style={{ fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 20, background: b.sc + '22', color: b.sc }}>{b.status}</span>
                {b.acts.map(a => (
                  <span key={a} style={{
                    fontSize: 11, padding: '3px 8px', borderRadius: 6, fontWeight: 500,
                    background: a === 'Cancel' ? '#fee2e2' : a === 'Confirm' ? '#10b981' : '#e5e7eb',
                    color: a === 'Cancel' ? '#dc2626' : a === 'Confirm' ? '#fff' : 'var(--text-secondary)',
                  }}>{a === 'Cancel' ? '✕' : '✓'} {a}</span>
                ))}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
          <div style={S.tip}>💡 Click any row to expand it — see all customer variables, add internal notes, and update the status. Filter buttons at the top let you focus on Pending or Confirmed bookings.</div>
          <div style={S.success}>✅ <strong>Confirm</strong> marks the booking as confirmed. <strong>✕ Cancel</strong> cancels the booking AND immediately releases the slot so other customers can enquire. <strong>Mark Complete</strong> closes the booking after the advertising run ends.</div>
        </div>
      </div>

      {/* Availability */}
      <div style={S.card}>
        <h2 style={S.h2}>🔒 Availability & Double-Booking Prevention</h2>
        <p style={S.p}>Two checks run every time a customer taps an item:</p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '16px' }}>
          {[
            { n: '1', col: '#f59e0b', t: 'Static availability field', d: <>If the <code>availability</code> column in the database is anything other than <code>Available</code> or <code>Yes</code>, the item is shown as unavailable immediately.</> },
            { n: '2', col: '#ef4444', t: 'Live booking lock', d: 'When a customer completes an enquiry, a lock is written to the database immediately. Any other customer who taps the same item sees the "unavailable" message until the booking is cancelled.' },
            { n: '3', col: '#10b981', t: 'Cancellation releases the slot', d: 'Clicking "✕ Cancel" in the Bookings tab removes the lock instantly. The item becomes available for new enquiries again.' },
          ].map(s => (
            <div key={s.n} style={S.step}>
              <div style={{ ...S.stepNum, background: s.col }}>{s.n}</div>
              <div>
                <div style={{ fontWeight: '600', color: 'var(--text-primary)', fontSize: '13px', marginBottom: '2px' }}>{s.t}</div>
                <div style={{ fontSize: '12px', color: 'var(--text-muted)', lineHeight: '1.5' }}>{s.d}</div>
              </div>
            </div>
          ))}
        </div>
        {/* Unavailable message mockup */}
        <div style={{ background: 'var(--bg-sidebar)', border: '1px solid var(--bg-element)', borderRadius: '10px', padding: '12px 14px', marginBottom: '14px' }}>
          <div style={{ fontSize: '11px', color: 'var(--text-faint)', marginBottom: '8px', fontWeight: '600', textTransform: 'uppercase' as const, letterSpacing: '.05em' }}>What the customer sees when a space is taken:</div>
          <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: '8px', overflow: 'hidden', maxWidth: '320px' }}>
            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#111', lineHeight: '1.6', borderBottom: '1px solid #f0f0f0' }}>
              <strong>Samora/Julius Nyerere</strong><br/>📌 Static Billboard · $450/month
            </div>
            <div style={{ padding: '8px 12px', fontSize: '12px', color: '#111', borderBottom: '1px solid #f0f0f0', lineHeight: '1.5' }}>
              ⚠️ This space is currently unavailable or booked. You can browse other options or contact us to join the waitlist.
            </div>
            <div style={{ padding: '7px 12px', fontSize: '11px', color: '#128C7E', borderBottom: '1px solid #f0f0f0', fontWeight: 500 }}>🗺️ Browse Other Spaces</div>
            <div style={{ padding: '7px 12px', fontSize: '11px', color: '#128C7E', fontWeight: 500 }}>📞 Contact Sales</div>
          </div>
        </div>
        <div style={S.warning}>⚠️ The lock is applied when the enquiry is <strong>submitted</strong>, not when you confirm it. This prevents race conditions where two customers enquire simultaneously. To re-open a slot, cancel the booking in the Bookings tab.</div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────
   SECTION → COMPONENT MAP
───────────────────────────────────────────── */
const SECTION_COMPONENTS: Record<string, React.FC> = {
  'overview': Overview,
  'creating-bot': CreatingBot,
  'bot-types': BotTypes,
  'dashboard': DashboardDoc,
  'flow-builder': FlowBuilderDoc,
  'response-types': ResponseTypesDoc,
  'templates': TemplatesDoc,
  'website-bot': WebsiteBotDoc,
  'whatsapp-bot': WhatsAppBotDoc,
  'facebook-bot': FacebookBotDoc,
  'voice-bot': VoiceBotDoc,
  'ai-bot': AIBotDoc,
  'advanced-wa': AdvancedWA,
  'order-handler': OrderHandlerDoc,
  'bookings-system': BookingsSystemDoc,
  'webhooks': WebhooksDoc,
  'analytics': AnalyticsDoc,
  'troubleshooting': TroubleshootingDoc,
  'faq': FAQDoc,
};

/* ─────────────────────────────────────────────
   MAIN COMPONENT
───────────────────────────────────────────── */
export default function Documentation() {
  const navigate = useNavigate();
  const { isDark } = useTheme();
  const [user, setUser] = useState<any>(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    const unsub = auth.onAuthStateChanged(u => setUser(u));
    return unsub;
  }, []);

  const handleLogout = async () => { await signOut(auth); navigate('/'); };

  const filteredSections = searchQuery
    ? sections.filter(s => s.title.toLowerCase().includes(searchQuery.toLowerCase()))
    : sections;

  const groups = Array.from(new Set(sections.map(s => s.group)));
  const ActiveContent = SECTION_COMPONENTS[activeSection] || Overview;
  const activeInfo = sections.find(s => s.id === activeSection);

  return (
    <>
      <SEO
        title={`Documentation | ${companyName}`}
        description={`Complete guide to using ${companyName} — build WhatsApp, website, Facebook, and voice bots.`}
        keywords="ZimChat docs, WhatsApp bot guide, chatbot documentation"
        type="website"
      />
      <div style={S.page}>

        {/* ── SIDEBAR ── */}
        <div style={S.sidebar(sidebarCollapsed)}>
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            style={{ position: 'absolute', right: '-13px', top: '22px', width: '26px', height: '26px', background: '#5B4FE8', border: 'none', borderRadius: '50%', color: 'white', cursor: 'pointer', zIndex: 10, fontSize: '11px' }}
          >
            {sidebarCollapsed ? '→' : '←'}
          </button>

          {/* Logo */}
          <div style={{ padding: sidebarCollapsed ? '20px 0' : '20px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
            {!sidebarCollapsed ? (
              <>
                <img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', display: 'block', maxWidth: '130px', objectFit: 'contain' }} />
                <div style={{ color: 'var(--text-faint)', fontSize: '11px', marginTop: '4px' }}>Complete guide</div>
              </>
            ) : <div style={{ textAlign: 'center' }}><img src={isDark ? '/Logo-white.png' : '/Logo-black.png'} alt={companyName} style={{ height: '68px', objectFit: 'contain' }} /></div>}
          </div>

          {/* Search */}
          {!sidebarCollapsed && (
            <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
              <input
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search docs..."
                style={{ width: '100%', padding: '7px 10px', background: 'var(--bg-element)', border: '1px solid var(--bg-element)', borderRadius: '7px', color: 'var(--text-primary)', fontSize: '12px', outline: 'none', boxSizing: 'border-box' }}
              />
            </div>
          )}

          {/* Nav */}
          <nav style={{ flex: 1, overflowY: 'auto', padding: sidebarCollapsed ? '12px 0' : '12px 8px' }}>
            {(searchQuery ? [{ group: 'Results', items: filteredSections }] : groups.map(g => ({ group: g, items: sections.filter(s => s.group === g) }))).map(({ group, items }) => (
              <div key={group}>
                {!sidebarCollapsed && (
                  <div style={{ fontSize: '9px', color: 'var(--nav-label)', textTransform: 'uppercase', letterSpacing: '0.08em', padding: '10px 8px 4px' }}>{group}</div>
                )}
                {(items as typeof sections).map(sec => (
                  <div
                    key={sec.id}
                    onClick={() => { setActiveSection(sec.id); setSearchQuery(''); }}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: sidebarCollapsed ? '10px 0' : '8px 10px',
                      borderRadius: '8px', cursor: 'pointer', marginBottom: '2px',
                      background: activeSection === sec.id ? '#5B4FE8' : 'transparent',
                      color: activeSection === sec.id ? 'var(--nav-item-active)' : 'var(--nav-item)',
                      justifyContent: sidebarCollapsed ? 'center' : 'flex-start',
                      transition: 'all 0.1s',
                    }}
                  >
                    <span style={{ fontSize: '14px' }}>{sec.icon}</span>
                    {!sidebarCollapsed && <span style={{ fontSize: '12px' }}>{sec.title}</span>}
                  </div>
                ))}
              </div>
            ))}
          </nav>

          {/* Footer */}
          {!sidebarCollapsed && (
            <div style={{ padding: '12px 16px', borderTop: '1px solid var(--border-subtle)' }}>
              <Link to="/errors" style={{ display: 'block', color: 'var(--text-faint)', fontSize: '12px', textDecoration: 'none', marginBottom: '6px' }}>🔴 Error Database</Link>
              <Link to="/dashboard" style={{ display: 'block', color: 'var(--text-faint)', fontSize: '12px', textDecoration: 'none', marginBottom: '6px' }}>← Back to dashboard</Link>
              <a href={`mailto:${companyEmail}`} style={{ display: 'block', color: 'var(--text-faint)', fontSize: '12px', textDecoration: 'none' }}>✉️ Email support</a>
            </div>
          )}
        </div>

        {/* ── MAIN ── */}
        <div style={S.main}>
          <div style={{ maxWidth: '800px', margin: '0 auto' }}>

            {/* Page header */}
            <div style={{ marginBottom: '28px' }}>
              <div style={{ fontSize: '36px', marginBottom: '10px' }}>{activeInfo?.icon}</div>
              <h1 style={S.h1}>{activeInfo?.title}</h1>
            </div>

            <ActiveContent />

            {/* Still need help */}
            <div style={{ ...S.card, textAlign: 'center', marginTop: '8px' }}>
              <div style={{ fontSize: '28px', marginBottom: '10px' }}>🆘</div>
              <div style={{ color: 'var(--text-primary)', fontWeight: '600', fontSize: '15px', marginBottom: '6px' }}>Still need help?</div>
              <p style={{ ...S.p, marginBottom: '16px' }}>Our team is happy to help you get your bot set up.</p>
              <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                <a href={`mailto:${companyEmail}`} style={{ padding: '9px 22px', background: '#5B4FE8', color: 'white', textDecoration: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: '600' }}>
                  ✉️ Email support
                </a>
                <Link to="/billing" style={{ padding: '9px 22px', background: 'var(--bg-element)', color: 'var(--text-secondary)', textDecoration: 'none', borderRadius: '8px', fontSize: '13px' }}>
                  💳 Upgrade plan
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}